// La sesión de Videos cortos (ADR-2026-10-30-videos-cortos-con-el-agente), calcada de
// `SesionDeCatalogo`. Dos fases:
//  • "conversando": la conversación a pantalla completa, hasta que el agente manda a escribir
//    los guiones (con "Adelante" eso pasa en el primer turno: la persona casi no la ve).
//  • "tanda": la conversación se achica al panel del costado (view transition) y entra la
//    tanda: el título, la configuración plegada (voz, subtítulos, indicación), una tarjeta 9:16
//    por producto con su guion, su video al salir y su pastilla de voz, y el pie con "Modificar"
//    (abre o cierra el agente) y Crear.
// La sesión ES un chat (tipo `videos-cortos`): al entrar se carga y, si el plan lo pide, se le
// manda el pedido de la portada EN SECUENCIA (cargar → confirmar que es este chat → mandar).
import { Loader2, MessageCircle, RefreshCw, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEntornoChatAtlas, useRegistrarPantalla, type ControladorDePantalla } from '../../../../chat-atlas';
import { useChatAtlas, useChatAtlasCascaron } from '../../../../chat-atlas/ChatAtlasContext';
import { ConversacionChatAtlas } from '../../../../chat-atlas/ui/ConversacionChatAtlas';
import CachedImg from '../../../../components/CachedImg';
import { CumbreMascot } from '../../../../components/CumbreMascot';
import { CreditBadge } from '../../../../components/CreditBadge';
import { EncabezadoPantalla } from '../../../../components/PantallaChrome';
import SubscriptionLimitModal from '../../../../components/SubscriptionLimitModal';
import { useSidebar } from '../../../../context/SidebarContext';
import { useDebajoDeMd } from '../../../../hooks/useEsMobile';
import { supabase } from '../../../../lib/supabase';
import { descargarArchivo } from '../../../../utils/descargarArchivo';
import { buildDownloadFileName } from '../../../../utils/downloadFileName';
import { useAgentEnv } from '../../agentEnvContext';
import { GeneratedImagesHistory } from '../../../GeneratedImagesHistory';
import { DescargarLosVideos } from './DescargarLosVideos';
import { TarjetaDeErrorDeGeneracion } from '../../components/TarjetaDeErrorDeGeneracion';
import { CajaDelAgenteMobile, HojaDelAgente, PastillaDelAgente, type EstadoDelAgenteMobile } from '../catalogoAgente/AgenteDelCatalogoMobile';
import { BORRADOR_VACIO, borradorDespuesDeEnviar, restaurarBorrador, type BorradorDelAgente } from '../catalogoAgente/borradorDelAgente';
import { estiloDelMarcoTelefono, MARCO_TELEFONO } from '../catalogoAgente/marcoDelTelefono';
import { alTocarLaPastilla, textoDeLaPastilla } from '../catalogoAgente/pastillaDelAgente';
import '../catalogoAgente/transicionDelCatalogo.css';
import { useTiraDeSidebar } from '../catalogoAgente/useTiraDeSidebar';
import { describirVideosCortos, nombreDeVoz, PANTALLA as PANTALLA_DE_VIDEOS } from './agente/describir';
import { ejecutarHerramientaDeVideosCortos } from './agente/ejecutar';
import { CLAVE_PANTALLA_VIDEOS_CORTOS, HERRAMIENTAS_DE_VIDEOS_CORTOS, SKILL_DE_VIDEOS_CORTOS } from './agente/herramientas';
import { ConfiguracionDeLaTanda, TituloEditable } from './ConfiguracionDeLaTanda';
import { PanelDeVideo } from './PanelDeVideo';
import { cambioDesdeElVideo, costoPorLanzar, productosPendientesDeEscritura, puedeRehacerSoloLaVoz, queFalta, videosEnCurso, videosPorLanzar, vozDelProducto, type ProductoDeLaTanda, type ProductoParaLaTanda } from './plan';
import type { EstadoDeLaPortadaDeVideos } from './PortadaDeVideosCortos';
import { RUTA_VIDEOS_CORTOS } from './rutas';
import { SelectorDeVoz } from './SelectorDeVoz';
import { TarjetaDeVideo } from './TarjetaDeVideo';
import { usePlanDeVideos } from './usePlanDeVideos';
import { useVideosFirmados } from './useVideosFirmados';
import { vocesDelEstudio, type VozDelEstudio } from './videosCortosApi';

export const NOMBRE_TRANSICION_PLAN = 'plan-del-catalogo';
export const NOMBRE_TRANSICION_CONVERSACION = 'conversacion-del-catalogo';
const PEDIDO_GENERICO = 'Planeá un video para cada producto que elegí: escribí los guiones y elegí la voz de cada uno.';
/** Cuánto se espera a que el agente dé señales antes de ofrecer volver a intentar. */
const ESPERA_ANTES_DE_REINTENTAR_MS = 15_000;

const dormir = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
const pideMenosMovimiento = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

type Fase = 'conversando' | 'tanda';

/** La tanda ya tiene algo escrito o creado: hay qué mostrar. */
const conGuiones = (p: { productos: ProductoDeLaTanda[] }): boolean => p.productos.some((x) => !!x.guion || x.estado !== 'sin-guion');

export const SesionDeVideosCortos: React.FC<{ sesionId: string }> = ({ sesionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const chat = useChatAtlas();
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const { abierto: panelAbierto, abrirChat, cerrarChat, fijarContexto } = useChatAtlasCascaron();
  const { companyId, brandId } = useAgentEnv();
  const [estadoDeLaPortada] = useState(() => location.state as Partial<EstadoDeLaPortadaDeVideos> | null);
  const sesion = usePlanDeVideos({ chatId: sesionId, companyId, planInicial: estadoDeLaPortada?.plan ?? null });
  const { plan, acciones, setPlan } = sesion;
  const esTelefono = useDebajoDeMd();
  const [agenteMobile, setAgenteMobile] = useState<EstadoDelAgenteMobile>('pastilla');
  const [borradorMobile, setBorradorMobile] = useState<BorradorDelAgente>(BORRADOR_VACIO);
  const [avisoMobile, setAvisoMobile] = useState<string | null>(null);

  // ── Las voces (las mismas del Estudio), una vez por sesión ────────────────
  const [voces, setVoces] = useState<VozDelEstudio[]>([]);
  const [cargandoVoces, setCargandoVoces] = useState(true);
  useEffect(() => {
    let vivo = true;
    vocesDelEstudio(companyId)
      .then((v) => vivo && setVoces(v))
      .catch(() => {})
      .finally(() => vivo && setCargandoVoces(false));
    return () => {
      vivo = false;
    };
  }, [companyId]);
  const vozLegible = useCallback((p: ProductoDeLaTanda) => (plan ? nombreDeVoz(vozDelProducto(plan, p), voces) : ''), [plan, voces]);

  // El panel del agente se agranda EN SU LUGAR, sobre la tanda, como en el Catálogo.
  const tituloDelPlan = plan?.titulo;
  useEffect(() => {
    fijarContexto({ pantalla: PANTALLA_DE_VIDEOS, ruta: location.pathname, ...(tituloDelPlan ? { detalle: `el lote «${tituloDelPlan}»` } : {}), agrandarEnElLugar: true });
  }, [fijarContexto, location.pathname, tituloDelPlan]);
  useEffect(() => () => fijarContexto(null), [fijarContexto]);

  // ── La fase ───────────────────────────────────────────────────────────────
  const [fase, setFase] = useState<Fase | null>(null);
  useEffect(() => {
    if (fase === null && plan) setFase(conGuiones(plan) ? 'tanda' : 'conversando');
  }, [fase, plan]);
  const corrimiento = useTiraDeSidebar(fase !== null);
  useEffect(() => {
    if (fase === 'conversando' && panelAbierto) cerrarChat();
    if (fase === 'tanda' && !panelAbierto && !esTelefono) abrirChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);
  useEffect(() => {
    if (esTelefono && fase === 'tanda' && panelAbierto) cerrarChat();
  }, [esTelefono, fase, panelAbierto, cerrarChat]);
  const { isCollapsed: sidebarColapsada, pedirExpandirSidebar } = useSidebar();
  const sidebarColapsadaAntes = useRef(sidebarColapsada);
  useEffect(() => {
    const seAbrio = sidebarColapsadaAntes.current && !sidebarColapsada;
    sidebarColapsadaAntes.current = sidebarColapsada;
    if (seAbrio && panelAbierto) cerrarChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarColapsada]);
  const alSalirRef = useRef<() => void>(() => {});
  alSalirRef.current = () => {
    if (panelAbierto && chatRef.current.motor.getChatId() === sesionId) cerrarChat();
    pedirExpandirSidebar();
  };
  const salidaPendiente = useRef<number | null>(null);
  useEffect(() => {
    if (salidaPendiente.current !== null) {
      window.clearTimeout(salidaPendiente.current);
      salidaPendiente.current = null;
    }
    return () => {
      salidaPendiente.current = window.setTimeout(() => alSalirRef.current(), 0);
    };
  }, []);

  const armada = !!plan && (sesion.escribiendo || conGuiones(plan));
  useEffect(() => {
    if (fase !== 'conversando' || !armada) return;
    const pasar = () => {
      setFase('tanda');
      if (!esTelefono) abrirChat();
    };
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    if (doc.startViewTransition && !pideMenosMovimiento()) doc.startViewTransition(() => flushSync(pasar));
    else pasar();
  }, [fase, armada, abrirChat, esTelefono]);

  // ── La sesión es el chat ──────────────────────────────────────────────────
  const esElChat = chat.motor.getChatId() === sesionId;
  const cargarChat = chat.cargarChat;
  useEffect(() => {
    if (chat.motor.getChatId() === sesionId) return;
    void cargarChat(sesionId)
      .then(() => {
        if (chat.motor.getChatId() !== sesionId) navigate(RUTA_VIDEOS_CORTOS, { replace: true, state: { aviso: 'Esa sesión no existe o no es de tu empresa.' } });
      })
      .catch(() => navigate(RUTA_VIDEOS_CORTOS, { replace: true, state: { aviso: 'No se pudo abrir la sesión. Intentá de nuevo.' } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId]);
  const asegurarElChat = useCallback(async (): Promise<boolean> => {
    for (let i = 0; i < 40 && chatRef.current.motor.getChatId() !== sesionId; i++) await dormir(100);
    if (chatRef.current.motor.getChatId() !== sesionId) await cargarChat(sesionId);
    await dormir(50);
    return chatRef.current.motor.getChatId() === sesionId;
  }, [cargarChat, sesionId]);
  const adjuntosDeLaPortada = useRef(estadoDeLaPortada?.primerMensaje?.adjuntos ?? []);
  const mandar = useCallback(
    async (texto: string, adjuntos: EstadoDeLaPortadaDeVideos['primerMensaje']['adjuntos'] = []): Promise<boolean> => {
      if (!(await asegurarElChat())) return false;
      if (chatRef.current.streaming) return chatRef.current.encolarMensaje(texto, adjuntos);
      chatRef.current.enviarMensaje(texto, adjuntos);
      return true;
    },
    [asegurarElChat],
  );

  // ── El pedido de la portada llega al agente solo, una vez ────────────────
  const pedidoEnCursoRef = useRef(false);
  const pedidoListo = !!plan && !plan.pedidoEnviado && !!plan.pedido.trim() && !conGuiones(plan);
  useEffect(() => {
    if (!pedidoListo || pedidoEnCursoRef.current || !plan) return;
    pedidoEnCursoRef.current = true;
    let cancelado = false;
    let mandado = false;
    void (async () => {
      if (!(await asegurarElChat()) || cancelado) return;
      if (chatRef.current.mensajes.length > 0) {
        mandado = true;
        setPlan((p) => ({ ...p, pedidoEnviado: true }));
        return;
      }
      mandado = true;
      setPlan((p) => ({ ...p, pedidoEnviado: true }));
      if (location.state) navigate(location.pathname, { replace: true, state: null });
      const adjuntos = adjuntosDeLaPortada.current;
      adjuntosDeLaPortada.current = [];
      chatRef.current.enviarMensaje(plan.pedido, adjuntos);
    })();
    return () => {
      cancelado = true;
      if (!mandado) pedidoEnCursoRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoListo]);

  // ── Lo que el agente está haciendo ───────────────────────────────────────
  const agenteTrabajando = esElChat && chat.streaming;
  const tarjetaPendiente = esElChat ? chat.motor.activeToolCard : null;
  useEffect(() => {
    if (!esTelefono && fase === 'tanda' && tarjetaPendiente && !panelAbierto) abrirChat();
  }, [esTelefono, fase, tarjetaPendiente, panelAbierto, abrirChat]);
  const vistosRef = useRef<number | null>(null);
  useEffect(() => {
    if (!esElChat) return;
    if (vistosRef.current === null || agenteMobile === 'chat') vistosRef.current = chat.mensajes.length;
  });
  const novedadMobile = esElChat && !agenteTrabajando && (!!tarjetaPendiente || chat.mensajes.length > (vistosRef.current ?? chat.mensajes.length));
  const sinArrancar = !!plan && !conGuiones(plan) && !agenteTrabajando && (!esElChat || chat.mensajes.length === 0);
  const [ofrecerReintento, setOfrecerReintento] = useState(false);
  useEffect(() => {
    if (!sinArrancar) {
      setOfrecerReintento(false);
      return;
    }
    const t = window.setTimeout(() => setOfrecerReintento(true), ESPERA_ANTES_DE_REINTENTAR_MS);
    return () => window.clearTimeout(t);
  }, [sinArrancar]);

  const reescribirConElAgente = useCallback(
    (producto: string) => {
      acciones.abrirProducto(null);
      const texto = `Reescribí el guion de ${producto}: `;
      if (esTelefono) {
        chatRef.current.setBorrador(texto);
        setAgenteMobile('chat');
        return;
      }
      abrirChat({ mensajeInicial: texto });
    },
    [acciones, abrirChat, esTelefono],
  );

  // ── El puente con el agente ───────────────────────────────────────────────
  const { escribiendo, generando, leerPlan } = sesion;
  const { productos: productosDeLaMarca } = useEntornoChatAtlas();
  const productosDeLaMarcaRef = useRef(productosDeLaMarca);
  productosDeLaMarcaRef.current = productosDeLaMarca;
  const buscarProductos = useCallback(async (ids: string[]) => {
    const encontrados: ProductoParaLaTanda[] = [];
    const noEncontrados: string[] = [];
    for (const id of ids) {
      const p = productosDeLaMarcaRef.current.find((x) => x.id === id);
      if (p) encontrados.push({ varianteId: p.id, nombre: p.name, ...(p.imageUrl ? { portadaUrl: p.imageUrl } : {}) });
      else noEncontrados.push(id);
    }
    return { encontrados, noEncontrados };
  }, []);
  const vocesRef = useRef(voces);
  vocesRef.current = voces;
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: CLAVE_PANTALLA_VIDEOS_CORTOS,
        skill: SKILL_DE_VIDEOS_CORTOS,
        describir: () => describirVideosCortos({ ruta: location.pathname, plan: leerPlan(), voces: vocesRef.current, enSesion: true, escribiendo, generando }),
        aplicar: () => ({ aplicados: [], fallidos: [] }),
        herramientas: HERRAMIENTAS_DE_VIDEOS_CORTOS,
        ejecutar: (nombre, input) =>
          ejecutarHerramientaDeVideosCortos(nombre, input, { plan: leerPlan()!, voces: vocesRef.current }, { ...acciones, leerPlan: () => leerPlan()!, buscarProductos }),
      }),
      [location.pathname, escribiendo, generando, acciones, leerPlan, buscarProductos],
    ),
  );

  // ── Crear, descargar, voces ───────────────────────────────────────────────
  // Los errores de Crear y de descargar van a la MISMA card que los modos de creación
  // (`TarjetaDeErrorDeGeneracion`), una sola vez: ni texto rojo en el pie ni el error en cada
  // tarjeta. Sin créditos, el modal de toda la app (como Animar).
  const [errorDeCrear, setErrorDeCrear] = useState<string | null>(null);
  const [sinCreditos, setSinCreditos] = useState<'free-trial-ended' | 'plan-limit-reached' | null>(null);
  const generar = useCallback(
    async (soloIds?: string[]) => {
      setErrorDeCrear(null);
      const r = await acciones.generar(soloIds);
      if (r.ok) return;
      if (r.sinCreditos) {
        const { data } = await supabase.from('company_profiles').select('plan').eq('id_company', companyId).maybeSingle();
        setSinCreditos((data as { plan?: string } | null)?.plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }
      setErrorDeCrear(r.error);
    },
    [acciones, companyId],
  );
  // El video abierto en el visor ampliado de la app (el de las creaciones, prestado por id como en el Catálogo); las
  // flechas siguen el orden de las tarjetas.
  const [videoAbierto, setVideoAbierto] = useState<string | null>(null);
  const ordenDelVisor = useMemo(() => (plan ? plan.productos.flatMap((p) => (p.estado === 'listo' && p.videoId ? [p.videoId] : [])) : []), [plan]);
  const rutas = useMemo(() => (plan ? plan.productos.flatMap((p) => (p.ruta ? [p.ruta] : [])) : []), [plan]);
  const videos = useVideosFirmados(rutas);
  const [bajando, setBajando] = useState<string | null>(null);
  const bajar = async (p: ProductoDeLaTanda) => {
    const v = p.ruta ? videos.get(p.ruta) : null;
    if (!v || bajando) return;
    setBajando(p.varianteId);
    try {
      await descargarArchivo({ url: v.url, fileName: buildDownloadFileName({ variantName: p.nombre, forceVideo: true }), esVideo: true });
    } catch {
      setErrorDeCrear('No se pudo descargar el video. Probá de nuevo en un momento.');
    } finally {
      setBajando(null);
    }
  };
  /** Para quién se está eligiendo la voz: `null` = la tanda; un id = ese producto. */
  const [eligiendoVoz, setEligiendoVoz] = useState<{ para: string | null } | null>(null);
  // El panel sigue montado mientras sale (la hoja anima la salida con `abierta={false}`), y se suelta
  // después: reabrirlo arranca de cero, con el guion de ese momento.
  const [panelSaliendo, setPanelSaliendo] = useState<string | null>(null);
  useEffect(() => {
    if (sesion.productoAbierto) {
      setPanelSaliendo(sesion.productoAbierto);
      return;
    }
    const t = window.setTimeout(() => setPanelSaliendo(null), 250);
    return () => window.clearTimeout(t);
  }, [sesion.productoAbierto]);

  if (sesion.noExiste) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <p className="text-[16px] font-semibold text-brand-black">Esta sesión no tiene un lote de videos</p>
        <button type="button" onClick={() => navigate(RUTA_VIDEOS_CORTOS)} className="rounded-md bg-brand-primary py-2.5 px-5 text-[15px] font-semibold text-white hover:bg-brand-primary-hover">
          Empezar una
        </button>
      </div>
    );
  }
  if (!plan || fase === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#3C3C43]/50" aria-hidden="true" />
      </div>
    );
  }

  const faltas = queFalta(plan);
  const pendientes = productosPendientesDeEscritura(plan).length;
  const porLanzar = videosPorLanzar(plan).length;
  const enCurso = videosEnCurso(plan);
  const costo = costoPorLanzar(plan);
  const sinGuiones = !conGuiones(plan);
  const estadoDeLaPastilla = { trabajando: agenteTrabajando, actividad: chat.actividad, pregunta: !!tarjetaPendiente, novedad: novedadMobile };
  const puedeCrear = faltas.length === 0 && porLanzar > 0 && !sesion.generando && !sesion.escribiendo;
  const productoAbierto = sesion.productoAbierto ? plan.productos.find((p) => p.varianteId === sesion.productoAbierto) ?? null : null;
  const idDelPanel = sesion.productoAbierto ?? panelSaliendo;
  const productoDelPanel = idDelPanel ? plan.productos.find((p) => p.varianteId === idDelPanel) ?? null : null;
  const productoDeLaVoz = eligiendoVoz?.para ? plan.productos.find((p) => p.varianteId === eligiendoVoz.para) ?? null : null;

  const avisoDeReintento = ofrecerReintento && (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#FFF8EB] py-3 px-4 text-[13.5px] text-brand-black">
      <span>El asistente todavía no arrancó con tu pedido.</span>
      <button
        type="button"
        onClick={() => {
          setOfrecerReintento(false);
          void mandar(plan.pedido.trim() || PEDIDO_GENERICO, adjuntosDeLaPortada.current);
        }}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white py-1.5 px-3.5 text-[13px] font-semibold text-brand-black shadow-sm ring-1 ring-black/[0.06] hover:bg-black/[0.03]"
      >
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
        Volver a intentar
      </button>
    </div>
  );

  // ── Conversando: la conversación del agente a pantalla completa ──────────
  if (fase === 'conversando') {
    const conFoto = plan.productos.filter((p) => p.portadaUrl);
    const sinMensajes = !esElChat || (chat.mensajes.length === 0 && !chat.streaming);
    return (
      <div className={`relative flex h-[100dvh] flex-col overflow-hidden bg-white ${MARCO_TELEFONO}`} style={{ viewTransitionName: NOMBRE_TRANSICION_CONVERSACION, ...estiloDelMarcoTelefono(esTelefono) }}>
        <div className="flex shrink-0 items-center gap-3 pr-6 pt-5" style={{ paddingLeft: corrimiento + 24 }}>
          <span className="flex shrink-0 items-center -space-x-2" aria-hidden="true">
            {conFoto.slice(0, 4).map((p, i) => (
              <span key={p.varianteId} className="block h-8 w-8 overflow-hidden rounded-lg bg-white shadow-sm ring-2 ring-white" style={{ zIndex: 4 - i }}>
                <CachedImg src={p.portadaUrl as string} alt="" className="h-full w-full object-cover" />
              </span>
            ))}
          </span>
          <p className="min-w-0 truncate text-[14px] font-semibold text-brand-black">
            {plan.titulo}
            <span className="font-normal text-[#3C3C43]/60">
              {' · '}
              {plan.productos.length} producto{plan.productos.length === 1 ? '' : 's'}
            </span>
          </p>
        </div>
        {avisoDeReintento && <div className="mx-auto w-full max-w-3xl shrink-0 px-6 pt-3">{avisoDeReintento}</div>}
        <div className="flex min-h-0 flex-1 flex-col">
          {sinMensajes ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3">
              <CumbreMascot state="thinking" size={88} decorativa />
              <p className="text-[14px] text-[#3C3C43]/60">Leyendo tu pedido…</p>
            </div>
          ) : (
            <ConversacionChatAtlas variante="pantalla" />
          )}
        </div>
      </div>
    );
  }

  const estadoDelPie = sinGuiones ? (
    agenteTrabajando ? (chat.actividad ?? 'El asistente está escribiendo los guiones…') : 'El asistente va a escribir los guiones.'
  ) : faltas.length > 0 ? (
    `Falta: ${faltas[0]}${faltas.length > 1 ? ` (+${faltas.length - 1})` : ''}`
  ) : porLanzar === 0 ? (
    enCurso > 0 ? `${enCurso} video${enCurso === 1 ? '' : 's'} creándose: cada uno tarda unos 5 minutos.` : 'Todos los videos salieron. Pedile cambios al asistente o rehacé uno desde su tarjeta.'
  ) : (
    `${porLanzar} video${porLanzar === 1 ? '' : 's'} por crear${enCurso > 0 ? ` · ${enCurso} creándose` : ''} · 12 créditos cada uno`
  );

  const botonCrear = (compacto: boolean) => (
    <button
      type="button"
      onClick={() => void generar()}
      disabled={!puedeCrear}
      title={faltas[0]}
      aria-label={`Crear ${porLanzar} video${porLanzar === 1 ? '' : 's'}`}
      className={`inline-flex items-center gap-2 bg-brand-primary font-semibold text-white shadow-sm transition-[background-color,transform] hover:bg-brand-primary-hover active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 ${
        compacto ? 'h-12 shrink-0 rounded-full px-5 text-[15px]' : 'rounded-xl py-3 px-6 text-[15px]'
      } ${sesion.generarPedido ? 'ring-4 ring-[rgb(var(--brand-primary-rgb)_/_0.25)]' : ''}`}
    >
      {sesion.generando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
      {sesion.generando ? 'Creando…' : compacto ? 'Crear' : `Crear ${porLanzar} video${porLanzar === 1 ? '' : 's'}`}
      {!sesion.generando && porLanzar > 0 && <CreditBadge credits={costo} tone="on-primary" size="sm" />}
    </button>
  );

  return (
    <div className={`relative flex h-[100dvh] flex-col overflow-hidden bg-white ${MARCO_TELEFONO}`} style={{ viewTransitionName: NOMBRE_TRANSICION_PLAN, paddingLeft: corrimiento, ...estiloDelMarcoTelefono(esTelefono) }}>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4 px-4 pb-32 md:px-6 md:pb-6">
          <div className="pt-1">
            <EncabezadoPantalla
              migas={[{ label: 'Inicio', onClick: () => navigate('/interfaz-v2') }, { label: 'Videos cortos', onClick: () => navigate(RUTA_VIDEOS_CORTOS) }, { label: plan.titulo }]}
              className="!px-0 !pb-1 hidden md:block"
            />
            <TituloEditable valor={plan.titulo} onGuardar={acciones.setTitulo} />
          </div>
          <ConfiguracionDeLaTanda
            plan={plan}
            nombreDeVoz={nombreDeVoz(plan.voz, voces)}
            escribiendo={sesion.escribiendo}
            pendientesDeEscritura={pendientes}
            onElegirVoz={() => setEligiendoVoz({ para: null })}
            onSubtitulos={acciones.setSubtitulos}
            onEscribir={() => void acciones.escribir()}
          />
          {avisoDeReintento}
          {plan.productos.length === 0 && <p className="text-[14px] text-[#3C3C43]/60">No quedó ningún producto en el lote.</p>}
          <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:flex sm:flex-wrap sm:gap-x-5 sm:gap-y-8">
            {plan.productos.map((p, i) => (
              <div key={p.varianteId} className="animate-fade-in" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}>
                <TarjetaDeVideo
                  producto={p}
                  nombreDeVoz={vozLegible(p)}
                  video={p.ruta ? videos.get(p.ruta) : null}
                  activa={sesion.productoAbierto === p.varianteId}
                  escribiendo={sesion.escribiendo}
                  onAbrir={() => acciones.abrirProducto(p.varianteId)}
                  onElegirVoz={() => setEligiendoVoz({ para: p.varianteId })}
                  onQuitarRehacer={() => acciones.quitarRehacer([p.varianteId])}
                  onDescargar={p.ruta ? () => void bajar(p) : undefined}
                  descargando={bajando === p.varianteId}
                  onExpandir={p.videoId ? () => setVideoAbierto(p.videoId!) : undefined}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hidden shrink-0 border-t border-black/[0.06] bg-white md:block">
        <div className="mx-auto flex w-full max-w-[1100px] items-center justify-between gap-4 px-6 py-4">
          <p className="min-w-0 truncate text-[13px] text-[#3C3C43]/60">{estadoDelPie}</p>
          <div className="flex shrink-0 items-center gap-2">
            <DescargarLosVideos plan={plan} companyId={companyId} />
            <button
              type="button"
              onClick={() => (panelAbierto ? cerrarChat() : abrirChat())}
              aria-pressed={panelAbierto}
              className={`inline-flex items-center gap-2 rounded-xl border py-3 px-6 text-[15px] font-semibold transition-colors ${
                panelAbierto ? 'border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.095)] text-brand-primary' : 'border-black/[0.1] bg-white text-brand-black hover:bg-black/[0.03]'
              }`}
            >
              {agenteTrabajando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <MessageCircle className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
              Modificar
            </button>
            {sesion.generarPedido && (
              <button type="button" onClick={acciones.cancelarGenerar} className="py-2 px-4 text-[14px] font-semibold text-[#3C3C43]/70 hover:text-brand-black">
                No crear
              </button>
            )}
            {botonCrear(false)}
          </div>
        </div>
      </div>

      {esTelefono && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 md:hidden">
          {faltas.length > 0 && !sinGuiones && (
            <p className="mx-3 mb-2 truncate rounded-full bg-white/95 py-1.5 px-3 text-[12.5px] text-[#3C3C43]/70 shadow-sm ring-1 ring-black/[0.06] backdrop-blur-md">
              Falta: {faltas[0]}
            </p>
          )}
          <div className="pointer-events-auto relative flex items-center gap-2 bg-gradient-to-t from-white via-white/85 to-transparent px-3 pt-4" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
            <PastillaDelAgente texto={textoDeLaPastilla(estadoDeLaPastilla)} trabajando={agenteTrabajando} novedad={novedadMobile} onAbrir={() => setAgenteMobile(alTocarLaPastilla(estadoDeLaPastilla))} compacta={porLanzar > 0 || sesion.generarPedido} />
            <DescargarLosVideos plan={plan} companyId={companyId} soloIconos />
            {sesion.generarPedido && (
              <button type="button" onClick={acciones.cancelarGenerar} aria-label="No crear" className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-[#3C3C43]/70 shadow-sm ring-1 ring-black/[0.06]">
                <X className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
              </button>
            )}
            {(porLanzar > 0 || sesion.generando || sesion.generarPedido) && <span className="ml-auto">{botonCrear(true)}</span>}
          </div>
        </div>
      )}
      {esTelefono && agenteMobile === 'caja' && (
        <CajaDelAgenteMobile
          borrador={borradorMobile}
          onCambiarBorrador={setBorradorMobile}
          aviso={avisoMobile}
          onCerrar={() => {
            setAvisoMobile(null);
            setAgenteMobile('pastilla');
          }}
          onVerConversacion={() => setAgenteMobile('chat')}
          onEnviar={(texto, adjuntos) => {
            const mandado = borradorMobile;
            setAvisoMobile(null);
            setBorradorMobile(borradorDespuesDeEnviar);
            setAgenteMobile('chat');
            void mandar(texto, adjuntos)
              .catch(() => false)
              .then((salio) => {
                if (salio) return;
                setBorradorMobile(restaurarBorrador(mandado));
                setAvisoMobile('No se pudo mandar. Probá de nuevo.');
                setAgenteMobile('caja');
              });
          }}
        />
      )}
      {esTelefono && <HojaDelAgente abierta={agenteMobile === 'chat'} onCerrar={() => setAgenteMobile('pastilla')} etiqueta="El asistente del lote" />}

      {productoDelPanel && (
        <PanelDeVideo
          abierta={!!productoAbierto}
          key={productoDelPanel.varianteId}
          producto={productoDelPanel}
          nombreDeVoz={vozLegible(productoDelPanel)}
          generando={sesion.generando}
          rehacerSugerido={cambioDesdeElVideo(plan, productoDelPanel) === 'voz' ? 'voz' : 'video'}
          puedeRehacerLaVoz={puedeRehacerSoloLaVoz(productoDelPanel)}
          onEditarGuion={(parcial) => acciones.editarGuion(productoDelPanel.varianteId, parcial)}
          onElegirVoz={() => setEligiendoVoz({ para: productoDelPanel.varianteId })}
          onRehacer={(modo) => {
            if (productoDelPanel.estado === 'listo') acciones.marcarRehacer([productoDelPanel.varianteId], { soloVoz: modo === 'voz' });
            acciones.abrirProducto(null);
            void generar([productoDelPanel.varianteId]);
          }}
          onReescribirConElAgente={() => reescribirConElAgente(productoDelPanel.nombre)}
          onQuitar={() => {
            acciones.abrirProducto(null);
            acciones.quitarProductos([productoDelPanel.varianteId]);
          }}
          onCerrar={() => acciones.abrirProducto(null)}
        />
      )}

      <SelectorDeVoz
        abierto={!!eligiendoVoz}
        para={productoDeLaVoz ? productoDeLaVoz.nombre : 'el lote'}
        voces={voces}
        cargando={cargandoVoces}
        elegida={productoDeLaVoz ? vozDelProducto(plan, productoDeLaVoz) : plan.voz}
        bajadaDeAutomatica={productoDeLaVoz ? 'Atlas elige según para quién es este producto' : undefined}
        onElegir={(voz) => acciones.setVoz(eligiendoVoz?.para ?? null, voz)}
        onCerrar={() => setEligiendoVoz(null)}
      />

      <TarjetaDeErrorDeGeneracion mensaje={errorDeCrear} onCerrar={() => setErrorDeCrear(null)} />
      {videoAbierto && (
        <GeneratedImagesHistory
          soloVisor={{
            imageId: videoAbierto,
            orden: ordenDelVisor,
            brandId: brandId ?? null,
            onCerrar: () => setVideoAbierto(null),
            onError: () => setVideoAbierto(null),
            // "Editar" en lugar de Corregir voz y Estudio: abre el panel del producto (idea, guion, voz y prompt) del
            // video que se está viendo, ENCIMA del visor, que queda abierto (ADR-2026-10-31-videos-cortos-editar-desde-
            // el-visor-2). Mientras el panel o el selector de voz están abiertos, el visor no escucha el teclado.
            editarVideo: (imagen) => {
              const producto = plan?.productos.find((x) => x.videoId === String(imagen.id));
              if (producto) acciones.abrirProducto(producto.varianteId);
            },
            tecladoEnPausa: !!productoAbierto || !!eligiendoVoz,
          }}
        />
      )}
      <SubscriptionLimitModal isOpen={sinCreditos !== null} onClose={() => setSinCreditos(null)} type={sinCreditos ?? 'plan-limit-reached'} />
    </div>
  );
};
